import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import {
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  CalendarIcon,
  DocumentTextIcon,
  HeartIcon,
  CurrencyDollarIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { clientsAPI } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import LoadingSpinner from '../../components/UI/LoadingSpinner';

interface ClientFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  gender: string;
  address: {
    street: string;
    city: string;
    state: string;
    zip: string;
  };
  emergencyContact: {
    name: string;
    relationship: string;
    phone: string;
  };
  careLevel: string;
  medicalConditions: string[];
  medications: string[];
  budget: {
    min: number;
    max: number;
  };
  preferences: {
    location: string;
    amenities: string[];
    roomType: string;
  };
  notes: string;
}

const ClientForm: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { success, error } = useToast();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  // Query for existing client data when in edit mode
  const { data: clientData, isLoading: isLoadingClient, error: clientError } = useQuery({
    queryKey: ['client', id],
    queryFn: () => clientsAPI.getClient(id!),
    enabled: isEditMode,
  });

  const [formData, setFormData] = useState<ClientFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    gender: '',
    address: {
      street: '',
      city: '',
      state: '',
      zip: ''
    },
    emergencyContact: {
      name: '',
      relationship: '',
      phone: ''
    },
    careLevel: 'independent',
    medicalConditions: [],
    medications: [],
    budget: {
      min: 0,
      max: 0
    },
    preferences: {
      location: '',
      amenities: [],
      roomType: 'private'
    },
    notes: ''
  });

  const [currentCondition, setCurrentCondition] = useState('');
  const [currentMedication, setCurrentMedication] = useState('');

  // Effect to populate form data when editing
  useEffect(() => {
    if (isEditMode && clientData?.data?.data?.client) {
      const client = clientData.data.data.client;
      
      // Parse medical notes back into conditions and medications
      const medicalConditions: string[] = [];
      const medications: string[] = [];
      let notes = '';
      
      if (client.medicalNotes) {
        const lines = client.medicalNotes.split('\n');
        lines.forEach((line: string) => {
          if (line.startsWith('Medical Condition: ')) {
            medicalConditions.push(line.replace('Medical Condition: ', ''));
          } else if (line.startsWith('Medication: ')) {
            medications.push(line.replace('Medication: ', ''));
          } else if (line.startsWith('Notes: ')) {
            notes = line.replace('Notes: ', '');
          }
        });
      }
      
      // Parse budget range back into min/max
      let budgetMin = 0;
      let budgetMax = 0;
      if (client.financialInfo?.budgetRange) {
        const budgetMatch = client.financialInfo.budgetRange.match(/\$(\d+)\s*-\s*\$(\d+)/);
        if (budgetMatch) {
          budgetMin = parseInt(budgetMatch[1]) || 0;
          budgetMax = parseInt(budgetMatch[2]) || 0;
        }
      }
      
      setFormData({
        firstName: client.firstName || '',
        lastName: client.lastName || '',
        email: client.email || '',
        phone: client.phone || '',
        dateOfBirth: client.dateOfBirth ? client.dateOfBirth.split('T')[0] : '',
        gender: client.gender || '',
        address: {
          street: client.address?.street || '',
          city: client.address?.city || '',
          state: client.address?.state || '',
          zip: client.address?.zip || ''
        },
        emergencyContact: {
          name: client.emergencyContact?.name || '',
          relationship: client.emergencyContact?.relationship || '',
          phone: client.emergencyContact?.phone || ''
        },
        careLevel: 'independent', // Default to independent since backend doesn't have this field
        medicalConditions,
        medications,
        budget: {
          min: budgetMin,
          max: budgetMax
        },
        preferences: {
          location: client.preferences?.location || '',
          amenities: client.preferences?.amenities || [],
          roomType: client.preferences?.roomType || 'private'
        },
        notes
      });
    }
  }, [isEditMode, clientData]);

  const createClientMutation = useMutation({
    mutationFn: clientsAPI.createClient,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.refetchQueries({ queryKey: ['clients'] });
      success('Client created successfully!');
      navigate('/clients');
    },
    onError: (err: any) => {
      console.error('🔍 DEBUG: Client creation error:', err);
      console.error('🔍 DEBUG: Error response:', err.response);
      console.error('🔍 DEBUG: Error response data:', err.response?.data);
      console.error('🔍 DEBUG: Validation errors:', err.response?.data?.errors);
      
      if (err.response?.data?.errors) {
        const errorMessages = err.response.data.errors.map((e: any) => e.msg).join(', ');
        error(`Validation errors: ${errorMessages}`);
      } else {
        error(err.response?.data?.message || err.message || 'Failed to create client');
      }
    }
  });

  const updateClientMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => 
      clientsAPI.updateClient(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['client', id] });
      queryClient.refetchQueries({ queryKey: ['clients'] });
      success('Client updated successfully!');
      navigate('/clients');
    },
    onError: (err: any) => {
      console.error('Error updating client:', err);
      
      if (err.response?.data?.errors) {
        const errorMessages = err.response.data.errors.map((e: any) => e.msg).join(', ');
        error(`Validation errors: ${errorMessages}`);
      } else {
        error(err.response?.data?.message || err.message || 'Failed to update client');
      }
    }
  });

  const handleInputChange = (field: string, value: any) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof ClientFormData] as object),
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const addMedicalCondition = () => {
    if (currentCondition.trim()) {
      setFormData(prev => ({
        ...prev,
        medicalConditions: [...prev.medicalConditions, currentCondition.trim()]
      }));
      setCurrentCondition('');
    }
  };

  const removeMedicalCondition = (index: number) => {
    setFormData(prev => ({
      ...prev,
      medicalConditions: prev.medicalConditions.filter((_, i) => i !== index)
    }));
  };

  const addMedication = () => {
    if (currentMedication.trim()) {
      setFormData(prev => ({
        ...prev,
        medications: [...prev.medications, currentMedication.trim()]
      }));
      setCurrentMedication('');
    }
  };

  const removeMedication = (index: number) => {
    setFormData(prev => ({
      ...prev,
      medications: prev.medications.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Debug: Log the current formData state
    console.log('🔍 DEBUG: Current formData state:', formData);
    console.log('🔍 DEBUG: formData keys:', Object.keys(formData));
    console.log('🔍 DEBUG: formData.firstName:', formData.firstName);
    console.log('🔍 DEBUG: formData.address:', formData.address);
    
    const transformedData = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      dateOfBirth: formData.dateOfBirth,
      gender: formData.gender,
      address: {
        street: formData.address.street.trim(),
        city: formData.address.city.trim(),
        state: formData.address.state.trim(),
        zip: formData.address.zip.trim()
      },
      emergencyContact: {
        name: formData.emergencyContact.name.trim(),
        relationship: formData.emergencyContact.relationship.trim(),
        phone: formData.emergencyContact.phone.trim()
      },
      medicalNotes: [
        ...formData.medicalConditions.map(condition => `Medical Condition: ${condition}`),
        ...formData.medications.map(medication => `Medication: ${medication}`),
        ...(formData.notes.trim() ? [`Notes: ${formData.notes.trim()}`] : [])
      ].join('\n'),
      financialInfo: {
        budgetRange: formData.budget.min && formData.budget.max 
          ? `$${formData.budget.min} - $${formData.budget.max}` 
          : ''
      }
    };

    console.log('🔍 DEBUG: Transformed data before API call:', transformedData);
    console.log('🔍 DEBUG: Transformed data keys:', Object.keys(transformedData));

    if (isEditMode && id) {
      updateClientMutation.mutate({ id, data: transformedData });
    } else {
      console.log('🔍 DEBUG: About to call createClientMutation.mutate with:', transformedData);
      createClientMutation.mutate(transformedData);
    }
  };

  if (createClientMutation.isPending || updateClientMutation.isPending || isLoadingClient) {
    return <LoadingSpinner />;
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate('/clients')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5" />
          Back to Clients
        </button>
        <h1 className="text-2xl font-bold text-gray-900 text-left">
          {isEditMode ? 'Edit Client' : 'Add New Client'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <UserIcon className="h-5 w-5" />
            Personal Information
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name *
              </label>
              <input
                type="text"
                required
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name *
              </label>
              <input
                type="text"
                required
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone *
              </label>
              <input
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="(555) 123-4567"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date of Birth *
              </label>
              <input
                type="date"
                required
                value={formData.dateOfBirth}
                onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Gender
              </label>
              <select
                value={formData.gender}
                onChange={(e) => handleInputChange('gender', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <MapPinIcon className="h-5 w-5" />
            Address Information
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Street Address
              </label>
              <input
                type="text"
                value={formData.address.street}
                onChange={(e) => handleInputChange('address.street', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <input
                type="text"
                value={formData.address.city}
                onChange={(e) => handleInputChange('address.city', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State
              </label>
              <input
                type="text"
                value={formData.address.state}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase().slice(0, 2);
                  handleInputChange('address.state', value);
                }}
                placeholder="CA"
                maxLength={2}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ZIP Code
              </label>
              <input
                type="text"
                value={formData.address.zip}
                onChange={(e) => handleInputChange('address.zip', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <PhoneIcon className="h-5 w-5" />
            Emergency Contact
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name *
              </label>
              <input
                type="text"
                required
                value={formData.emergencyContact.name}
                onChange={(e) => handleInputChange('emergencyContact.name', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Relationship *
              </label>
              <input
                type="text"
                required
                value={formData.emergencyContact.relationship}
                onChange={(e) => handleInputChange('emergencyContact.relationship', e.target.value)}
                placeholder="e.g., Spouse, Child, Sibling"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number *
              </label>
              <input
                type="tel"
                required
                value={formData.emergencyContact.phone}
                onChange={(e) => handleInputChange('emergencyContact.phone', e.target.value)}
                placeholder="(555) 123-4567"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <HeartIcon className="h-5 w-5" />
            Medical Information
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Care Level
              </label>
              <select
                value={formData.careLevel}
                onChange={(e) => handleInputChange('careLevel', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="independent">Independent Living</option>
                <option value="assisted">Assisted Living</option>
                <option value="memory">Memory Care</option>
                <option value="skilled">Skilled Nursing</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Medical Conditions
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={currentCondition}
                  onChange={(e) => setCurrentCondition(e.target.value)}
                  placeholder="Enter medical condition"
                  className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addMedicalCondition())}
                />
                <button
                  type="button"
                  onClick={addMedicalCondition}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.medicalConditions.map((condition, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm"
                  >
                    {condition}
                    <button
                      type="button"
                      onClick={() => removeMedicalCondition(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Medications
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={currentMedication}
                  onChange={(e) => setCurrentMedication(e.target.value)}
                  placeholder="Enter medication"
                  className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addMedication())}
                />
                <button
                  type="button"
                  onClick={addMedication}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.medications.map((medication, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                  >
                    {medication}
                    <button
                      type="button"
                      onClick={() => removeMedication(index)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <CurrencyDollarIcon className="h-5 w-5" />
            Budget Information
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Minimum Budget (Monthly)
              </label>
              <input
                type="number"
                min="0"
                value={formData.budget.min}
                onChange={(e) => handleInputChange('budget.min', parseInt(e.target.value) || 0)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Maximum Budget (Monthly)
              </label>
              <input
                type="number"
                min="0"
                value={formData.budget.max}
                onChange={(e) => handleInputChange('budget.max', parseInt(e.target.value) || 0)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <DocumentTextIcon className="h-5 w-5" />
            Preferences
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Preferred Location
              </label>
              <input
                type="text"
                value={formData.preferences.location}
                onChange={(e) => handleInputChange('preferences.location', e.target.value)}
                placeholder="e.g., Near downtown, Specific neighborhood"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Room Type Preference
              </label>
              <select
                value={formData.preferences.roomType}
                onChange={(e) => handleInputChange('preferences.roomType', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="private">Private Room</option>
                <option value="semi-private">Semi-Private Room</option>
                <option value="shared">Shared Room</option>
                <option value="studio">Studio Apartment</option>
                <option value="one-bedroom">One Bedroom</option>
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Additional Notes
          </h2>
          
          <textarea
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            rows={4}
            placeholder="Any additional information about the client's needs, preferences, or special considerations..."
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/clients')}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createClientMutation.isPending || updateClientMutation.isPending}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {(createClientMutation.isPending || updateClientMutation.isPending) 
              ? (isEditMode ? 'Updating...' : 'Creating...') 
              : (isEditMode ? 'Update Client' : 'Create Client')
            }
          </button>
        </div>
      </form>
    </div>
  );
};

export default ClientForm;
